import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';

import { API_BASE_URL, MOCK_MODE, PARTIAL_MOCK } from '@/config/env';
import { ROUTE_TREE } from '@/config/routeTree';
import { configureSession, getAuthToken, signOut, watchFirebaseSession } from '@/features/auth/session';
import { restoreBrandColors } from '@/features/organization/brandTheme';
import { configureFirestoreStream } from '@/hooks/useLiveData';
import { activeLanguage } from '@/i18n';
import { configureApiClient } from '@/lib/apiClient';
import { createQueryClient } from '@/lib/queryClient';
import '@/styles/fonts.css';
import '@/styles/global.css';
import '@/styles/tokens.css';
import { AppThemeProvider } from '@/theme/AppThemeProvider';

configureApiClient({
  getAuthToken,
  getQueryDefaults: () => ({ language: activeLanguage() }),
  onUnauthorized: () => signOut('expired'),
});

configureFirestoreStream({ onPermissionDenied: () => signOut('denied') });

restoreBrandColors();

const queryClient = createQueryClient();

configureSession({ onSessionEnd: () => queryClient.clear() });

async function start() {
  await watchFirebaseSession();

  if (MOCK_MODE || PARTIAL_MOCK) {
    const { worker } = await import('@/mocks/browser');
    await worker.start({
      // Warns on an unhandled API call in full mock mode; static assets and partial
      // mode bypass silently.
      onUnhandledRequest(request, print) {
        if (!PARTIAL_MOCK && API_BASE_URL && request.url.startsWith(API_BASE_URL)) {
          print.warning();
        }
      },
    });
  }

  const root = document.getElementById('root');
  if (!root) throw new Error('The page has no #root element.');
  createRoot(root).render(
    <StrictMode>
      <AppThemeProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={createBrowserRouter(ROUTE_TREE)} />
        </QueryClientProvider>
      </AppThemeProvider>
    </StrictMode>,
  );
}

void start();
